import Quill, { Delta } from 'quill/core';
import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { useQuery } from '@apollo/client';
import useLegacyIsLoggedIn from '../account/useIsLoggedIn';
import useVisitorSession from '../account/useVisitorSession';
import useAppSelector, { useAppDispatch } from '../app/redux/useAppSelector';
import useTestHelpersInDev from '../dev/useTestHelpersInDev';
import { AssetTokenProvider } from '../editor/AssetTokenContext';
import type { TextChangeHandler } from '../editor/Editor';
import Editor from '../editor/Editor';
import QuillContext, { useQuill } from '../editor/QuillContext';
import UIElementResizeContext from '../editor/UIElementResizeContext';
import WiderLineSizeContext from '../editor/WiderLineSizeContext';
import { EditorActions } from '../editor/editorSlice';
import useAnchorScrollingHandler, {
  useScrollingToAnchorOnLoad,
} from '../editor/useAnchorScrollingHandler';
import NotFoundView from '../error/NotFoundView';
import { gql } from '../graphql/generated/gql';
import { PostLinkAccess } from '../graphql/generated/graphql';
import { useAddPublicRecentlyViewedPost } from '../public/PublicSearchModal';
import useIsPublicDocumentationView from '../public/useIsPublicDocumentationView';
import { UIActions } from '../ui/uiSlice';
import PageMeta from '../utils/PageMeta';
import useNavigate from '../utils/router/useNavigate';
import useSlug from '../utils/router/useSlug';
import postPath from '../utils/url/postPath';
import HoverContentPreview from './HoverContentPreview';
import MagneticWall from './MagneticWall';
import PendingMagneticWallItemProvider from './PendingMagneticWallItemProvider';
import PostLayout from './PostLayout';
import PostLayoutProvider from './PostLayoutProvider';
import PostSkeleton from './PostSkeleton';
import PostStickyControl, { TOCInMagneticWall } from './PostStickyControl';
import { PostViewContainer } from './PostView';
import PostViewBanner from './PostViewBanner';
import { FooterControlBar } from './controlBar/ControlBar';
import PostSeriesFooter from './series/PostSeriesFooter';
import PostTopicListWithBreadcrumbs from './topic/PostTopicListWithBreadcrumbs';
import useCachedPostTitle from './useCachedPostTitle';
import { useHasHeadings } from './useHasHeadings';
import useHasMagneticWallItems from './useHasMagneticWallItems';
import type { ExpandableAreas } from './usePostLayoutStateReducer';
import useShareSecret from './useShareSecret';
import useShouldShowEditorPlaceholder from './utils/useShouldShowEditorPlaceholder';

const PUBLIC_POST_QUERY = gql(/** @complexity 33 */ `
  query PublicPostView($id: ID!, $shareSecret: String) {
    post: publicPost(id: $id, shareSecret: $shareSecret) {
      id
      linkAccess
      publishedAt
      content(isLatest: true) {
        id
        delta
        version
      }
      banner {
        original
      }
      title
      series {
        id
        posts {
          id
          topics {
            id
          }
        }
        ...PostTopicListWithBreadcrumbs_postSeries
        ...PostTopicListWithBreadcrumbs_postSeriesDetails
        ...FooterControlBar_series
        ...FooterControlBar_seriesDetails
      }
      topics {
        id
        ...PostTopicListWithBreadcrumbs_topic
      }
      ...postPath_post
      ...EditorActions_post
      ...Editor_post
      ...useHasMagneticWallItems_post
    }
    entityAssetToken(entityId: $id, entityType: POST, shareSecret: $shareSecret) {
      ...AssetTokenProvider_entityAssetToken
    }
  }
`);

interface InnerPublicPostViewProps {
  postId: string;
}

const InnerPublicPostView = ({ postId }: InnerPublicPostViewProps) => {
  const { visitorSession, ensureVisitorSession } = useVisitorSession(postId);

  const { quill } = useQuill();
  const { name: organizationName } = window.__slab.PUBLIC_ORG;
  const readOnly = useAppSelector((state) => state.editor.readOnly);
  const isVersionLoaded = useAppSelector((state) => state.editor.version > -1);
  const isLocked = useAppSelector(
    (state) => state.session.organization.isLocked,
  );
  const isLoggedIn = useLegacyIsLoggedIn();
  const isPublicDocumentationView = useIsPublicDocumentationView();
  const navigate = useNavigate();

  const shareSecret = useShareSecret();
  const { data, loading } = useQuery(PUBLIC_POST_QUERY, {
    variables: { id: postId, shareSecret },
    fetchPolicy: 'network-only',
  });

  const post = data?.post;

  const entityAssetToken = data?.entityAssetToken;

  const series = useMemo(() => {
    if (!post) {
      return undefined;
    }
    // Only posts with public topics could be shown in public series components
    const postsWithPublicTopic = post.series?.posts.filter(
      (p) => p.topics.length > 0,
    );

    return postsWithPublicTopic?.some((p) => p.id === post.id) && post.series
      ? // Type casting correct __typename for PostSeriesFooter and PostSeriesBar
        { ...post.series, posts: postsWithPublicTopic }
      : undefined;
  }, [post]);

  const isEditMode = !readOnly && !!visitorSession;

  const dispatch = useAppDispatch();

  const postLayoutRootRef = useRef(null);
  const innerBannerRef = useRef<HTMLDivElement | null>(null);
  const cachedPostTitle = useCachedPostTitle(postId);

  useEffect(() => {
    if (!post) return undefined;
    dispatch(UIActions.setExternalTopbarLoginRedirectPath(postPath(post)));
    return () => {
      dispatch(UIActions.setExternalTopbarLoginRedirectPath());
    };
  }, [dispatch, post]);

  // Remote updates not applied yet
  const receivedRef = useRef(new Delta());

  const addPublicRecentlyViewedPost = useAddPublicRecentlyViewedPost();
  useEffect(() => {
    if (post?.id) addPublicRecentlyViewedPost(post.id);
  }, [addPublicRecentlyViewedPost, post?.id]);

  useScrollingToAnchorOnLoad(quill);

  const quillRef = useRef(quill);
  useEffect(() => {
    quillRef.current = quill;
    return () => {
      quillRef.current = null;
    };
  }, [quill]);
  useTestHelpersInDev();

  useEffect(() => {
    const { hash } = document.location;
    if (hash) {
      const header = document.getElementById(hash.slice(1));
      if (header != null) {
        header.scrollIntoView();
      }
    }
  }, [post]);

  const isPostEditable =
    !!post && !isLocked && post.linkAccess === PostLinkAccess.PublicEdit;

  const loadedPostIdRef = useRef<string | null>(null);
  const hasMagneticWallItems = useHasMagneticWallItems(post, !isEditMode);
  const hasHeadings = useHasHeadings();

  const shouldNavigateToInternal =
    post && isVersionLoaded && !readOnly && isLoggedIn;

  useEffect(() => {
    if (shouldNavigateToInternal) {
      navigate(postPath(post), { state: { edit: true } });
    }
  }, [navigate, post, shouldNavigateToInternal]);

  useEffect(() => {
    if (
      !shouldNavigateToInternal &&
      !readOnly &&
      isPostEditable &&
      ensureVisitorSession
    ) {
      ensureVisitorSession();
    }
  }, [
    shouldNavigateToInternal,
    ensureVisitorSession,
    isPostEditable,
    readOnly,
  ]);

  useEffect(() => {
    if (post && loadedPostIdRef.current !== post.id) {
      if (!isEditMode) {
        dispatch(
          EditorActions.loadInitialContent({
            initialContent: post.content.delta,
            initialVersion: post.content.version,
            editable: isPostEditable,
          }),
        );
        if (readOnly && !post.publishedAt && isPostEditable) {
          dispatch(EditorActions.readOnly(false));
        }
      } else {
        dispatch(
          EditorActions.load({
            post,
            isEditable: isPostEditable,
            isPublicView: true,
            onRemoteDelta: (delta: Delta) => {
              receivedRef.current = receivedRef.current.compose(delta);
              quillRef.current?.updateContents(
                receivedRef.current,
                Quill.sources.API,
              );
            },
          }),
        );
        loadedPostIdRef.current = post.id;
      }
    }
  }, [dispatch, isEditMode, isPostEditable, post, readOnly]);

  useEffect(
    () => () => {
      dispatch(EditorActions.leave());
    },
    [dispatch, postId],
  );

  const anchorScrollingHandlerProps = useAnchorScrollingHandler(quill, postId, {
    updateLocationHash: true,
  });
  const handleTextChange: TextChangeHandler = useCallback(
    (_delta, _oldContent, source) => {
      if (source === Quill.sources.API) {
        receivedRef.current = new Delta();
      }
    },
    [],
  );

  const shouldShowPlaceholder = useShouldShowEditorPlaceholder();

  if (!post && !loading) {
    return <NotFoundView />;
  }

  const currentBannerUrl = post?.banner?.original;
  const hasBanner = currentBannerUrl != null;
  const postTitle = post?.title || cachedPostTitle;
  const expandableAreas: ExpandableAreas = isPublicDocumentationView
    ? {
        sidebar: true,
        toc: false,
        magneticWall: hasHeadings,
      }
    : {
        sidebar: false,
        toc: hasHeadings,
        magneticWall: hasMagneticWallItems,
      };

  return (
    <PostLayoutProvider expandableAreas={expandableAreas}>
      <PendingMagneticWallItemProvider>
        <PostViewContainer
          className="public-post"
          {...anchorScrollingHandlerProps}
        >
          {postTitle && (
            <PageMeta title={`${postTitle} - ${organizationName}`} />
          )}
          <PostLayout.Root
            hasBanner={hasBanner}
            ref={postLayoutRootRef}
            forceExpandOnMonitor={
              !isPublicDocumentationView ||
              // To better center post content, expand if neither TOC nor Magnetic Wall is expanded
              (!hasHeadings && !hasMagneticWallItems)
            }
          >
            {isVersionLoaded && post ? (
              <WiderLineSizeContext
                containerRef={postLayoutRootRef}
                editor={quill ? quill.scroll.domNode : null}
              >
                <UIElementResizeContext>
                  {currentBannerUrl && (
                    <PostLayout.Banner>
                      <PostViewBanner
                        banner={currentBannerUrl}
                        setInnerBannerRef={innerBannerRef}
                      />
                    </PostLayout.Banner>
                  )}
                  <PostLayout.Above>
                    <PostTopicListWithBreadcrumbs
                      postId={post.id}
                      postTopics={post.topics}
                      postSeries={series}
                      postSeriesDetails={series}
                    />
                  </PostLayout.Above>
                  <PostLayout.TOC>
                    {!isPublicDocumentationView && (
                      <PostStickyControl
                        postId={post.id}
                        hasBanner={hasBanner}
                      />
                    )}
                  </PostLayout.TOC>
                  <PostLayout.MagneticWall>
                    {isPublicDocumentationView ? (
                      <TOCInMagneticWall
                        postId={post.id}
                        hasBanner={hasBanner}
                      />
                    ) : (
                      isEditMode &&
                      quill && (
                        <MagneticWall
                          quill={quill}
                          post={post}
                          canEditPost={isPostEditable}
                          innerBannerRef={innerBannerRef}
                          hasBanner={hasBanner}
                        />
                      )
                    )}
                  </PostLayout.MagneticWall>
                  <PostLayout.Editor hasBanner={hasBanner}>
                    <HoverContentPreview>
                      {entityAssetToken && (
                        <AssetTokenProvider entityAssetToken={entityAssetToken}>
                          <Editor
                            post={post}
                            namespace="publicEditor"
                            showPlaceholder={shouldShowPlaceholder}
                            onTextChange={handleTextChange}
                          />
                        </AssetTokenProvider>
                      )}
                    </HoverContentPreview>
                  </PostLayout.Editor>
                  <PostLayout.Below>
                    <FooterControlBar
                      postId={post.id}
                      series={series}
                      seriesDetails={series}
                    />
                    {series && (
                      <PostSeriesFooter postId={post.id} series={series} />
                    )}
                  </PostLayout.Below>
                </UIElementResizeContext>
              </WiderLineSizeContext>
            ) : (
              <PostLayout.Editor>
                <PostLayout.Above />
                <PostSkeleton />
              </PostLayout.Editor>
            )}
          </PostLayout.Root>
        </PostViewContainer>
      </PendingMagneticWallItemProvider>
    </PostLayoutProvider>
  );
};

const PublicPostView = () => {
  const [postId] = useSlug('postSlug');

  return (
    <QuillContext>
      <InnerPublicPostView key={postId} postId={postId} />
    </QuillContext>
  );
};

export default PublicPostView;
